import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Text,
  VStack,
  HStack,
  Icon,
  Button,
  Divider,
  Badge,
  SimpleGrid,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { FaBolt, FaArrowLeft, FaExclamationTriangle } from 'react-icons/fa';
import useEnergy from '../hooks/useEnergy';

function EnergyDetails() {
  const location = useLocation();
  const { state } = location;
  const address = state?.address;

  const { epcData, isLoading, error, refreshData } = useEnergy(address);

  if (isLoading) {
    return (
      <Box
        bg='brand.background'
        minH='100vh'
        py={8}
        display='flex'
        justifyContent='center'
        alignItems='center'
      >
        <VStack spacing={4}>
          <Spinner size='xl' color='brand.primary' thickness='4px' />
          <Text>Loading energy data...</Text>
        </VStack>
      </Box>
    );
  }

  if (error || !epcData) {
    return (
      <Box bg='brand.background' minH='100vh' py={8}>
        <Container maxW='container.lg'>
          <Link to='/'>
            <Button
              leftIcon={<Icon as={FaArrowLeft} />}
              colorScheme='gray'
              variant='outline'
              mb={6}
            >
              Back to Overview
            </Button>
          </Link>

          <Alert
            status='error'
            variant='subtle'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
            textAlign='center'
            height='200px'
            borderRadius='md'
          >
            <AlertIcon boxSize='40px' mr={0} />
            <AlertTitle mt={4} mb={1} fontSize='lg'>
              Energy Data Not Available
            </AlertTitle>
            <AlertDescription maxWidth='sm'>
              {error || "We couldn't find energy data for this property."}
            </AlertDescription>
            <Button mt={4} colorScheme='red' onClick={refreshData}>
              Try Again
            </Button>
          </Alert>
        </Container>
      </Box>
    );
  }

  const propertyData = epcData.property_data;
  const areaSummary = epcData.area_summary;

  // Calculate total annual cost
  const totalAnnualCost =
    propertyData.heating_cost_current +
    propertyData.hot_water_cost_current +
    propertyData.lighting_cost_current;

  // Calculate potential savings
  const potentialSavings =
    propertyData.heating_cost_current -
    propertyData.heating_cost_potential +
    (propertyData.hot_water_cost_current - propertyData.hot_water_cost_potential) +
    (propertyData.lighting_cost_current - propertyData.lighting_cost_potential);

  return (
    <Box bg='brand.background' minH='100vh' py={8}>
      <Container maxW='container.lg'>
        {/* Back Button */}
        <Link to='/'>
          <Button leftIcon={<Icon as={FaArrowLeft} />} colorScheme='gray' variant='outline' mb={6}>
            Back to Overview
          </Button>
        </Link>

        {/* Main Content */}
        <Card variant='outline' mb={6} bg='brand.section'>
          <CardHeader>
            <HStack spacing={3}>
              <Icon as={FaBolt} boxSize={6} color='brand.primary' />
              <Heading size='lg' color='brand.text'>
                Energy Details
              </Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <VStack spacing={8} align='stretch'>
              {/* Property Details Section */}
              <Box>
                <Heading size='md' mb={4} color='brand.text'>
                  Property Details
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <HStack justify='space-between'>
                    <Text color='brand.text'>Floor Area:</Text>
                    <Text fontWeight='semibold'>{propertyData.total_floor_area} m²</Text>
                  </HStack>
                  <HStack justify='space-between'>
                    <Text color='brand.text'>Habitable Rooms:</Text>
                    <Text fontWeight='semibold'>{propertyData.number_habitable_rooms}</Text>
                  </HStack>
                  <HStack justify='space-between'>
                    <Text color='brand.text'>Main Fuel:</Text>
                    <Text fontWeight='semibold'>{propertyData.main_fuel}</Text>
                  </HStack>
                  <HStack justify='space-between'>
                    <Text color='brand.text'>Extensions:</Text>
                    <Text fontWeight='semibold'>{propertyData.extension_count}</Text>
                  </HStack>
                  <HStack justify='space-between'>
                    <Text color='brand.text'>Inspection Date:</Text>
                    <Text fontWeight='semibold'>{propertyData.inspection_date}</Text>
                  </HStack>
                  <HStack justify='space-between'>
                    <Text color='brand.text'>Property Typex:</Text>
                    <Text fontWeight='semibold'>{propertyData.property_type}</Text>
                  </HStack>
                  <HStack justify='space-between'>
                    <Text color='brand.text'>Built Form:</Text>
                    <Text fontWeight='semibold'>{propertyData.built_form}</Text>
                  </HStack>
                </SimpleGrid>
              </Box>

              <Divider />

              {/* Energy Efficiency Section */}
              <Box>
                <Heading size='md' mb={4} color='brand.text'>
                  Energy Efficiency
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <HStack justify='space-between'>
                    <Text color='brand.text'>Current Energy Rating:</Text>
                    <Badge
                      bg={getRatingColor(propertyData.current_energy_rating)}
                      color='white'
                      fontSize='md'
                    >
                      {propertyData.current_energy_rating} ({propertyData.current_energy_efficiency}
                      )
                    </Badge>
                  </HStack>
                  <HStack justify='space-between'>
                    <Text color='brand.text'>Potential Energy Rating:</Text>
                    <Badge
                      bg={getRatingColor(propertyData.potential_energy_rating)}
                      color='white'
                      fontSize='md'
                    >
                      {propertyData.potential_energy_rating} (
                      {propertyData.potential_energy_efficiency})
                    </Badge>
                  </HStack>
                  <HStack justify='space-between'>
                    <Text color='brand.text'>Hot Water Efficiency:</Text>
                    <Badge
                      bg={getEfficiencyColor(propertyData.hot_water_env_eff)}
                      color='white'
                      fontSize='md'
                    >
                      {propertyData.hot_water_env_eff}
                    </Badge>
                  </HStack>
                  <HStack justify='space-between'>
                    <Text color='brand.text'>Heating Controls Efficiency:</Text>
                    <Badge
                      bg={getEfficiencyColor(propertyData.mainheatc_energy_eff)}
                      color='white'
                      fontSize='md'
                    >
                      {propertyData.mainheatc_energy_eff}
                    </Badge>
                  </HStack>
                </SimpleGrid>
              </Box>

              <Divider />

              {/* Annual Energy Costs Section */}
              <Box>
                <Heading size='md' mb={4} color='brand.text'>
                  Annual Energy Costs
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <HStack justify='space-between'>
                    <Text color='brand.text'>Heating:</Text>
                    <Text fontWeight='semibold' color='red.500'>
                      £{propertyData.heating_cost_current}
                    </Text>
                  </HStack>
                  <HStack justify='space-between'>
                    <Text color='brand.text'>Hot Water:</Text>
                    <Text fontWeight='semibold' color='blue.500'>
                      £{propertyData.hot_water_cost_current}
                    </Text>
                  </HStack>
                  <HStack justify='space-between'>
                    <Text color='brand.text'>Lighting:</Text>
                    <Text fontWeight='semibold' color='yellow.500'>
                      £{propertyData.lighting_cost_current}
                    </Text>
                  </HStack>
                  <HStack justify='space-between'>
                    <Text color='brand.text'>Total Annual Cost:</Text>
                    <Text fontWeight='bold' color='green.500'>
                      £{totalAnnualCost.toFixed(2)}
                    </Text>
                  </HStack>
                  <HStack justify='space-between'>
                    <Text color='brand.text'>Potential Savings:</Text>
                    <Text fontWeight='bold' color='green.500'>
                      £{potentialSavings.toFixed(2)}
                    </Text>
                  </HStack>
                </SimpleGrid>
              </Box>

              <Divider />

              {/* Property Features Section */}
              <Box>
                <Heading size='md' mb={4} color='brand.text'>
                  Property Features
                </Heading>
                <VStack spacing={4} align='stretch'>
                  <HStack justify='space-between'>
                    <Text color='brand.text'>Walls:</Text>
                    <Text fontWeight='semibold'>{propertyData.walls_description}</Text>
                  </HStack>
                  <HStack justify='space-between'>
                    <Text color='brand.text'>Walls Efficiency:</Text>
                    <Badge bg={getEfficiencyColor(propertyData.walls_energy_eff)} color='white'>
                      {propertyData.walls_energy_eff}
                    </Badge>
                  </HStack>
                  <HStack justify='space-between'>
                    <Text color='brand.text'>Roof:</Text>
                    <Text fontWeight='semibold'>{propertyData.roof_description}</Text>
                  </HStack>
                  <HStack justify='space-between'>
                    <Text color='brand.text'>Roof Efficiency:</Text>
                    <Badge bg={getEfficiencyColor(propertyData.roof_energy_eff)} color='white'>
                      {propertyData.roof_energy_eff}
                    </Badge>
                  </HStack>
                  <HStack justify='space-between'>
                    <Text color='brand.text'>Windows:</Text>
                    <Text fontWeight='semibold'>{propertyData.windows_description}</Text>
                  </HStack>
                  <HStack justify='space-between'>
                    <Text color='brand.text'>Windows Efficiency:</Text>
                    <Badge bg={getEfficiencyColor(propertyData.windows_energy_eff)} color='white'>
                      {propertyData.windows_energy_eff}
                    </Badge>
                  </HStack>
                </VStack>
              </Box>

              {areaSummary && (
                <>
                  <Divider />

                  {/* Area Comparison Section */}
                  <Box>
                    <Heading size='md' mb={4} color='brand.text'>
                      Area Comparison
                    </Heading>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <HStack justify='space-between'>
                        <Text color='brand.text'>Average Energy Rating:</Text>
                        <Text fontWeight='semibold'>{areaSummary.average_energy_rating}</Text>
                      </HStack>
                      <HStack justify='space-between'>
                        <Text color='brand.text'>Average Floor Area:</Text>
                        <Text fontWeight='semibold'>{areaSummary.average_floor_area} m²</Text>
                      </HStack>
                      <HStack justify='space-between'>
                        <Text color='brand.text'>Average Heating Cost:</Text>
                        <Text fontWeight='semibold'>£{areaSummary.average_heating_cost}</Text>
                      </HStack>
                      <HStack justify='space-between'>
                        <Text color='brand.text'>Average Habitable Rooms:</Text>
                        <Text fontWeight='semibold'>{areaSummary.average_habitable_rooms}</Text>
                      </HStack>
                      <HStack justify='space-between'>
                        <Text color='brand.text'>Total Certificates in Area:</Text>
                        <Text fontWeight='semibold'>{areaSummary.total_certificates}</Text>
                      </HStack>
                    </SimpleGrid>
                  </Box>
                </>
              )}
            </VStack>
          </CardBody>
        </Card>
      </Container>
    </Box>
  );
}

// Helper function to get color based on rating
function getRatingColor(rating) {
  const colors = {
    A: '#00a652',
    B: '#50b848',
    C: '#c0d731',
    D: '#fbf23b',
    E: '#f8961d',
    F: '#f36f21',
    G: '#ed1c24',
  };

  return colors[rating] || '#999999';
}

// Helper function to get color based on efficiency text
function getEfficiencyColor(efficiency) {
  const colors = {
    'Very Good': '#00a652',
    Good: '#50b848',
    Average: '#fbf23b',
    Poor: '#f8961d',
    'Very Poor': '#ed1c24',
    'N/A': '#999999',
  };

  return colors[efficiency] || '#999999';
}

export default EnergyDetails;
